import React from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { DeleteOutline as DeleteOutlineIcon } from "../../../../../components/icons";
import { useCateringStore } from "../../../../../store/useCateringStore";
import Input from "../../../../../components/Input";
import { useCateringMenuItems } from "../../../../../hooks/useCateringMenuItems";
import {
  OperationsDetailField,
  OperationsDetailGrid,
  OperationsSectionHeader,
  operationsRowSx,
  operationsTableHeadSx,
  operationsTablePaperSx,
} from "../../../ifas/components/OperationsUI";

const unitOptions = [
  "pcs",
  "plates",
  "bowls",
  "trays",
  "pots",
  "portions",
  "kg",
  "litres",
  "loaves",
  "Other",
];

const getSetupTimeFromServingTime = (servingTime: string) => {
  if (!servingTime || !servingTime.includes(":")) return "";

  const [hours, minutes] = servingTime.split(":").map(Number);

  if (Number.isNaN(hours) || Number.isNaN(minutes)) return "";

  const totalMinutes = hours * 60 + minutes - 150;
  const normalizedMinutes = ((totalMinutes % 1440) + 1440) % 1440;
  const setupHours = Math.floor(normalizedMinutes / 60);
  const setupMinutes = normalizedMinutes % 60;

  return `${String(setupHours).padStart(2, "0")}:${String(
    setupMinutes,
  ).padStart(2, "0")}`;
};

export default function CateringSetup({
  isEditable = false,
}: {
  isEditable?: boolean;
}) {
  const {
    selectedCatering,
    setCateringSetup,
    setCateringStaffing,
    setCateringMenu,
    addCateringMenuItem,
    updateCateringMenuItem,
    removeCateringMenuItem,
  } = useCateringStore();
  const item = selectedCatering?.cateringSetup;
  const menu = selectedCatering?.menu;
  const items = menu?.items || [];
  const { menuItems, loading } = useCateringMenuItems(
    menu?.menuCategoryDisplayName,
  );

  const isPrefilled = Boolean(selectedCatering?.cateringSetup);

  const getSelectedMenuValue = (menuItem: (typeof items)[number]) => {
    if (menuItem.templateItemId) return menuItem.templateItemId;

    const matchedMenuItem = menuItems.find(
      (templateItem) => templateItem.itemName === menuItem.itemName,
    );

    return matchedMenuItem?.templateItemId || "";
  };

  const handleChange =
    (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (field === "servingTime") {
        const calculatedSetupTime = getSetupTimeFromServingTime(value);

        setCateringSetup({
          servingTime: value,
          setupTime: calculatedSetupTime,
          servingStyle: "Buffet",
        });
        setCateringStaffing({
          staffArrivalTime: calculatedSetupTime,
        });
        return;
      }

      setCateringSetup({
        [field]:
          field === "guestCount" || field === "servingStations"
            ? Number(value) || ""
            : value,
      });
    };

  const handlePackageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;

    setCateringMenu({
      menuCategoryDisplayName: nextValue,
      items: items.map((menuItem) => ({
        ...menuItem,
        templateItemId: "",
        itemName: "",
      })),
    });
    setCateringSetup({
      menuCategoryName: nextValue,
      menuCategoryId: "",
    });
  };

  if (!isEditable) {
    return (
      <Box>
        <OperationsSectionHeader title="Serving Setup" />
        <OperationsDetailGrid>
          <OperationsDetailField
            label="Serving Style"
            value={item?.servingStyle || "Buffet"}
          />
          <OperationsDetailField
            label="Number of Guests"
            value={item?.guestCount}
          />
          <OperationsDetailField
            label="Serving Stations"
            value={item?.servingStations}
          />
          <OperationsDetailField label="Serving Time" value={item?.servingTime} />
          <OperationsDetailField label="Setup Time" value={item?.setupTime} />
          <OperationsDetailField
            label="Breakdown Time"
            value={item?.breakdownTime}
          />
          <OperationsDetailField
            label="Selected Package"
            value={menu?.menuCategoryDisplayName}
            span={2}
          />
        </OperationsDetailGrid>

        <Box mt={2.5}>
          <OperationsSectionHeader title="Menu Items" />
          <Paper sx={operationsTablePaperSx} elevation={0}>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead sx={operationsTableHeadSx}>
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length > 0 ? (
                    items.map((menuItem, index) => (
                      <TableRow key={index} sx={operationsRowSx}>
                        <TableCell>{menuItem.itemName || "Not captured"}</TableCell>
                        <TableCell>{menuItem.quantity || "Not captured"}</TableCell>
                        <TableCell>{menuItem.unit || "Not captured"}</TableCell>
                        <TableCell>{menuItem.notes || "Not captured"}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        No menu items captured.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        <Box mt={2.5}>
          <OperationsDetailGrid>
            <OperationsDetailField
              label="Notes"
              value={item?.customRequests}
              span={3}
            />
          </OperationsDetailGrid>
        </Box>
      </Box>
    );
  }

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={item?.servingStyle || "Buffet"}
          height={40}
          width="100%"
          label="Serving Style"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={item?.guestCount ?? ""}
          onChange={handleChange("guestCount")}
          type="number"
          height={40}
          width="100%"
          label="Number of Guests"
          sx={{
            backgroundColor: isEditable ? "white" : "#f5f5f5",
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          value={item?.servingStations ?? ""}
          onChange={handleChange("servingStations")}
          height={40}
          width="100%"
          label="Number of Serving Stations"
          backgroundColor="background.paper"
        >
          <MenuItem value={2}>2</MenuItem>
          <MenuItem value={3}>3</MenuItem>
          <MenuItem value={4}>4</MenuItem>
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={item?.servingTime || ""}
          onChange={handleChange("servingTime")}
          type="time"
          height={40}
          width="100%"
          label="Serving Time"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled
          value={item?.setupTime || ""}
          type="time"
          height={40}
          width="100%"
          label="Setup Time"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          value={item?.breakdownTime || ""}
          onChange={handleChange("breakdownTime")}
          type="time"
          height={40}
          width="100%"
          label="Breakdown Time"
          backgroundColor="background.paper"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box>

          <Grid
            container
            spacing={3}
            mb={3}
            alignItems="flex-end"
            justifyContent="space-between"
          >
            <Grid size={{ xs: 12, md: 4 }}>
              <Input
                select
                value={menu?.menuCategoryDisplayName || ""}
                onChange={handlePackageChange}
                height={40}
                width="100%"
                label="Selected Package"
                backgroundColor="background.paper"
              >
                <MenuItem value="Izandla">Izandla</MenuItem>
                <MenuItem value="Menu 2">Menu 2</MenuItem>
                <MenuItem value="Menu 3">Menu 3</MenuItem>
                <MenuItem value="Menu 4">Menu 4</MenuItem>
              </Input>
            </Grid>
            <Grid size={{ xs: 12, md: "auto" }}>
              <Button
                variant="contained"
                color="secondary"
                onClick={addCateringMenuItem}
                sx={{ textTransform: "none", minWidth: 140, height: 40 }}
              >
                Add Item
              </Button>
            </Grid>
          </Grid>

          <Paper sx={{ background: "#ffffff", overflowX: "auto" }} elevation={2}>
            <TableContainer
              sx={{
                maxHeight: 420,
                overflowX: "auto",
              }}
            >
              <Table sx={{ width: "100%" }}>
                <TableHead
                  sx={{
                    minWidth: "max-content",
                    "& .MuiTableCell-root": {
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell>Item Name</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.length > 0 ? (
                    items.map((menuItem, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {(() => {
                            const selectedValue = getSelectedMenuValue(menuItem);
                            const hasMatchingOption = menuItems.some(
                              (templateItem) =>
                                templateItem.templateItemId === selectedValue,
                            );

                            return (
                              <Input
                                select
                                value={selectedValue}
                                onChange={(
                                  event: React.ChangeEvent<HTMLInputElement>,
                                ) => {
                                  const selectedMenuItem = menuItems.find(
                                    (templateItem) =>
                                      templateItem.templateItemId ===
                                      event.target.value,
                                  );

                                  updateCateringMenuItem(index, {
                                    templateItemId: event.target.value,
                                    itemName: selectedMenuItem?.itemName || "",
                                  });
                                }}
                                height={40}
                                width="100%"
                                backgroundColor="background.paper"
                                disabled={loading}
                                SelectProps={{
                                  displayEmpty: true,
                                  renderValue: (selected: unknown) => {
                                    const selectedMenuItem = menuItems.find(
                                      (templateItem) =>
                                        templateItem.templateItemId ===
                                        String(selected),
                                    );

                                    return (
                                      selectedMenuItem?.itemName ||
                                      menuItem.itemName ||
                                      "Select menu item"
                                    );
                                  },
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        backgroundColor: "#ffffff",
                                        borderRadius: "12px",
                                        maxHeight: "300px",
                                      },
                                    },
                                  },
                                }}
                              >
                                {!hasMatchingOption && menuItem.itemName ? (
                                  <MenuItem
                                    value={
                                      selectedValue || menuItem.id || "current"
                                    }
                                  >
                                    {menuItem.itemName}
                                  </MenuItem>
                                ) : null}
                                {menuItems.map((templateItem) => (
                                  <MenuItem
                                    key={templateItem.templateItemId}
                                    value={templateItem.templateItemId}
                                  >
                                    {templateItem.itemName}
                                  </MenuItem>
                                ))}
                              </Input>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={menuItem.quantity}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateCateringMenuItem(index, {
                                quantity:
                                  Number(event.target.value) || undefined,
                              })
                            }
                            type="number"
                            height={40}
                            width="100%"
                            backgroundColor="background.paper"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            select
                            value={menuItem.unit}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateCateringMenuItem(index, {
                                unit: event.target.value,
                              })
                            }
                            height={40}
                            width="100%"
                            backgroundColor="background.paper"
                          >
                            {unitOptions.map((unit) => (
                              <MenuItem key={unit} value={unit}>
                                {unit}
                              </MenuItem>
                            ))}
                          </Input>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={menuItem.notes || ""}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                              updateCateringMenuItem(index, {
                                notes: event.target.value || null,
                              })
                            }
                            height={40}
                            width="100%"
                            backgroundColor="background.paper"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            onClick={() => removeCateringMenuItem(index)}
                            color="error"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        No menu items yet. Click Add Item to create the first
                        row.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Grid>

      <Grid size={{ xs: 12, md: 12 }}>
        <Input
          value={item?.customRequests || ""}
          onChange={handleChange("customRequests")}
          height={100}
          width="100%"
          label="Notes"
          placeholder="Enter notes"
          backgroundColor="background.paper"
          multiline
          inputProps={{ maxLength: 150 }}
          helperText={`${item?.customRequests?.length || 0}/150`}
        />
      </Grid>
    </Grid>
  );
}
